import type { DirectoryUser } from "./access.ts";

export const USERS_FILTER_COLUMNS = ["user", "status", "roles"] as const;
export type UsersFilterColumn = (typeof USERS_FILTER_COLUMNS)[number];

export type UsersFilters = { readonly values: ReadonlyMap<UsersFilterColumn, ReadonlySet<string>> };
export const NO_USERS_FILTERS: UsersFilters = { values: new Map() };

export type UsersSort = { readonly column: UsersFilterColumn; readonly direction: "asc" | "desc" };
export const DEFAULT_USERS_SORT: UsersSort = { column: "user", direction: "asc" };

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export function userStatusFacet(user: DirectoryUser): string {
  return user.disabled ? "Disabled" : "Active";
}

export function userFacetValue(user: DirectoryUser, column: UsersFilterColumn): string {
  switch (column) {
    case "user":
      return user.userName;
    case "status":
      return userStatusFacet(user);
    case "roles":
      return user.roles.join(", ");
  }
}

function rowFacets(user: DirectoryUser): ReadonlyMap<UsersFilterColumn, string> {
  return new Map(USERS_FILTER_COLUMNS.map((column) => [column, userFacetValue(user, column)]));
}

function passes(facets: ReadonlyMap<UsersFilterColumn, string>, column: UsersFilterColumn, filters: UsersFilters): boolean {
  const selected = filters.values.get(column);
  if (!selected || selected.size === 0) return true;
  return selected.has(facets.get(column) ?? "");
}

export function filterUserRows(rows: readonly DirectoryUser[], filters: UsersFilters): DirectoryUser[] {
  return rows.filter((row) => {
    const facets = rowFacets(row);
    return USERS_FILTER_COLUMNS.every((column) => passes(facets, column, filters));
  });
}

export type UsersColumnFacet = { readonly options: readonly { value: string; label: string; count: number }[] };
export type UsersFacets = { readonly rows: readonly DirectoryUser[]; readonly columns: ReadonlyMap<UsersFilterColumn, UsersColumnFacet> };

export function computeUsersFacets(rows: readonly DirectoryUser[], filters: UsersFilters): UsersFacets {
  const counts = new Map<UsersFilterColumn, Map<string, number>>(USERS_FILTER_COLUMNS.map((column) => [column, new Map()]));
  const passing: DirectoryUser[] = [];
  for (const row of rows) {
    const facets = rowFacets(row);
    const failed = USERS_FILTER_COLUMNS.filter((column) => !passes(facets, column, filters));
    if (failed.length === 0) passing.push(row);
    for (const column of USERS_FILTER_COLUMNS) {
      const counted = failed.length === 0 || (failed.length === 1 && failed[0] === column);
      const tally = counts.get(column);
      if (!tally) continue;
      const value = facets.get(column) ?? "";
      tally.set(value, (tally.get(value) ?? 0) + (counted ? 1 : 0));
    }
  }
  const columns = new Map<UsersFilterColumn, UsersColumnFacet>();
  for (const column of USERS_FILTER_COLUMNS) {
    const tally = counts.get(column) ?? new Map<string, number>();
    columns.set(column, {
      options: [...tally.keys()].sort((a, b) => collator.compare(a, b)).map((value) => ({ value, label: value, count: tally.get(value) ?? 0 })),
    });
  }
  return { rows: passing, columns };
}

function filterSignature(filters: UsersFilters): string {
  const parts: string[] = [];
  for (const column of USERS_FILTER_COLUMNS) {
    const selected = filters.values.get(column);
    if (!selected || selected.size === 0) continue;
    parts.push(column, ...[...selected].sort());
  }
  return parts.join("\u0000");
}

export function createUsersFacetMemo() {
  let last: { rows: readonly DirectoryUser[]; signature: string; facets: UsersFacets } | null = null;
  return (rows: readonly DirectoryUser[], filters: UsersFilters): UsersFacets => {
    const signature = filterSignature(filters);
    if (last && last.rows === rows && last.signature === signature) return last.facets;
    const facets = computeUsersFacets(rows, filters);
    last = { rows, signature, facets };
    return facets;
  };
}

export function toggleUsersFilter(filters: UsersFilters, column: UsersFilterColumn, value: string): UsersFilters {
  const values = new Map(filters.values);
  const selected = new Set(values.get(column) ?? []);
  if (selected.has(value)) selected.delete(value);
  else selected.add(value);
  if (selected.size === 0) values.delete(column);
  else values.set(column, selected);
  return { values };
}

export function selectedUsersFilterCount(filters: UsersFilters, column: UsersFilterColumn): number {
  return filters.values.get(column)?.size ?? 0;
}

export function hasUsersFilters(filters: UsersFilters): boolean {
  return [...filters.values.values()].some((selected) => selected.size > 0);
}

export function sortUserRows(rows: readonly DirectoryUser[], sort: UsersSort): DirectoryUser[] {
  const copy = [...rows];
  const direction = sort.direction === "asc" ? 1 : -1;
  copy.sort((left, right) => collator.compare(userFacetValue(left, sort.column), userFacetValue(right, sort.column)) * direction);
  return copy;
}

export const USERS_COLUMN_LABELS: Readonly<Record<UsersFilterColumn, string>> = {
  user: "User",
  status: "Status",
  roles: "Roles",
};
